import { GAME_CONFIG } from './config.js';

export const TERRAIN_TYPES = Object.freeze({
  WATER: 0,
  FLOOR: 1,
  DARK_FLOOR: 2,
  STONE: 3
});

export const TERRAIN_HEIGHTS = Object.freeze([-4, 0, 1.5, 3]);

function mix32(value) {
  let x = value | 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  return (x ^ (x >>> 16)) >>> 0;
}

export function hash2D(x, y, seed = GAME_CONFIG.worldSeed) {
  return mix32(
    Math.imul(x | 0, 0x1f123bb5) ^
    Math.imul(y | 0, 0x5f356495) ^
    seed
  ) / 0x1_0000_0000;
}

function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

function valueNoise(x, y, scale) {
  const sx = x / scale;
  const sy = y / scale;
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const tx = smoothstep(sx - x0);
  const ty = smoothstep(sy - y0);
  const a = hash2D(x0, y0);
  const b = hash2D(x0 + 1, y0);
  const c = hash2D(x0, y0 + 1);
  const d = hash2D(x0 + 1, y0 + 1);
  const top = a + (b - a) * tx;
  const bottom = c + (d - c) * tx;
  return top + (bottom - top) * ty;
}

export function terrainNoise(x, y) {
  return valueNoise(x, y, 260) * 0.6 +
    valueNoise(x + 913, y - 421, 110) * 0.3 +
    valueNoise(x - 117, y + 719, 55) * 0.1;
}

export function terrainAt(x, y) {
  const distanceToSafeZone = Math.hypot(
    x - GAME_CONFIG.safeZone.x,
    y - GAME_CONFIG.safeZone.y
  );
  const noise = terrainNoise(x, y);
  const zone = GAME_CONFIG.zone;
  if (x < zone.minX || x > zone.maxX || y < zone.minY || y > zone.maxY) {
    return { type: TERRAIN_TYPES.WATER, height: TERRAIN_HEIGHTS[TERRAIN_TYPES.WATER], walkable: false };
  }
  let type = noise < 0.62
      ? TERRAIN_TYPES.FLOOR
      : noise < 0.84
        ? TERRAIN_TYPES.DARK_FLOOR
        : TERRAIN_TYPES.STONE;

  const villageRoad = Math.abs(x - 500) < 72 && y > -850 && y < 1550;
  const eastRoad = Math.abs(y - 520 - Math.sin((x - 500) / 180) * 45) < 62 && x > 430 && x < 1760;
  const quarryRoad = Math.abs(x - (500 + (y - 500) * 0.45)) < 70 && y > 500 && y < 1500;
  const village = distanceToSafeZone < GAME_CONFIG.safeZone.radius;

  if (village || villageRoad || eastRoad || quarryRoad) {
    type = village && distanceToSafeZone < 175 ? TERRAIN_TYPES.STONE : TERRAIN_TYPES.DARK_FLOOR;
  } else if (
    Math.pow((x + 470) / 240, 2) + Math.pow((y - 180) / 150, 2) < 1 ||
    Math.pow((x - 1600) / 190, 2) + Math.pow((y + 430) / 135, 2) < 1
  ) {
    type = TERRAIN_TYPES.WATER;
  } else if (type === TERRAIN_TYPES.STONE && y < 850) {
    type = TERRAIN_TYPES.FLOOR;
  }

  if (distanceToSafeZone < GAME_CONFIG.safeZone.radius && type === TERRAIN_TYPES.WATER) {
    type = TERRAIN_TYPES.FLOOR;
  }
  if (distanceToSafeZone < 45) type = TERRAIN_TYPES.FLOOR;

  return {
    type,
    height: TERRAIN_HEIGHTS[type],
    walkable: type !== TERRAIN_TYPES.WATER
  };
}

export function chunkCoords(x, y) {
  return {
    x: Math.floor(x / GAME_CONFIG.chunkSize),
    y: Math.floor(y / GAME_CONFIG.chunkSize)
  };
}

export function chunkKey(x, y) {
  return `${x},${y}`;
}

export function deterministicSpawn(chunkX, chunkY, index) {
  const size = GAME_CONFIG.chunkSize;
  for (let attempt = 0; attempt < 16; attempt += 1) {
    const salt = index * 37 + attempt;
    const x = chunkX * size + 80 + hash2D(chunkX + salt, chunkY - salt, 0xa531) * (size - 160);
    const y = chunkY * size + 80 + hash2D(chunkX - salt, chunkY + salt, 0x1c69) * (size - 160);
    if (terrainAt(x, y).walkable &&
        Math.hypot(x - GAME_CONFIG.safeZone.x, y - GAME_CONFIG.safeZone.y) >
          GAME_CONFIG.safeZone.radius + 80) {
      return { x, y };
    }
  }
  return {
    x: chunkX * size + size / 2,
    y: chunkY * size + size / 2
  };
}
